import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { adminApi } from "./api";

const STATUSES = ["", "new", "in_review", "contacted", "closed"];
const LABEL = { "": "All", new: "New", in_review: "In review", contacted: "Contacted", closed: "Closed" };

function prettySource(src) {
  if (!src) return null;
  if (src === "contact_form") return null;
  if (src === "website_assistant") return "Website assistant";
  return src.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function EnquiriesList() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const q = params.get("q") || "";
  const page = Number(params.get("page") || 1);

  const [data, setData] = useState({ items: [], total: 0, page_size: 20 });
  const [loading, setLoading] = useState(false);
  const [qLocal, setQLocal] = useState(q);

  useEffect(() => {
    setLoading(true);
    adminApi
      .listEnquiries({ status: status || undefined, q: q || undefined, page, page_size: 20 })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [status, q, page]);

  const setFilter = (patch) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === "" || v == null) next.delete(k);
      else next.set(k, String(v));
    }
    if (!("page" in patch)) next.delete("page");
    setParams(next);
  };

  const totalPages = Math.max(1, Math.ceil(data.total / (data.page_size || 20)));

  return (
    <div className="ga-page" data-testid="enquiries-page">
      <div className="ga-eyebrow">Enquiries</div>
      <h1 className="ga-title">Inbox</h1>

      <div className="ga-banner" data-testid="inbox-email-banner" style={{ marginBottom: 24 }}>
        <span className="ga-banner__dot" aria-hidden="true" />
        Email notifications are disabled by owner decision. Enquiries must be checked here regularly.
      </div>

      <div className="ga-toolbar">
        <div className="ga-filters" role="tablist" aria-label="Status filter" data-testid="status-filters">
          {STATUSES.map((s) => (
            <button
              type="button"
              key={s || "all"}
              className={`ga-chip ${status === s ? "ga-chip--active" : ""}`}
              onClick={() => setFilter({ status: s })}
              data-testid={`filter-${s || "all"}`}
            >
              {LABEL[s]}
            </button>
          ))}
        </div>
        <form
          className="ga-search"
          onSubmit={(e) => {
            e.preventDefault();
            setFilter({ q: qLocal.trim() });
          }}
        >
          <input
            type="search"
            placeholder="Search name, email or company"
            value={qLocal}
            onChange={(e) => setQLocal(e.target.value)}
            data-testid="search-input"
          />
          <button type="submit" className="ga-btn ga-btn--ghost" data-testid="search-submit">
            Search
          </button>
        </form>
      </div>

      <div className="ga-table" data-testid="enquiries-table">
        <div className="ga-table__head">
          <div>Received</div>
          <div>Name</div>
          <div>Company</div>
          <div>Category</div>
          <div>Status</div>
          <div>Actions</div>
        </div>
        {loading ? (
          <div className="ga-table__empty">Loading…</div>
        ) : data.items.length === 0 ? (
          <div className="ga-table__empty" data-testid="enquiries-empty">No enquiries found.</div>
        ) : (
          data.items.map((it) => {
            const srcLabel = prettySource(it.source);
            return (
              <div className="ga-table__row" key={it.id} data-testid={`row-${it.id}`}>
                <div className="ga-mute">{fmt(it.created_at)}</div>
                <div>
                  <div className="ga-name-row">
                    <span>{it.name}</span>
                    {srcLabel && (
                      <span className="ga-source-chip" data-testid={`source-${it.id}`} title={`Submitted via ${srcLabel}`}>
                        {srcLabel}
                      </span>
                    )}
                  </div>
                  <div className="ga-mute">{it.email}</div>
                </div>
                <div>{it.company || <span className="ga-mute">—</span>}</div>
                <div>{it.category}</div>
                <div>
                  <span className={`ga-badge ga-badge--${it.status}`}>{LABEL[it.status] || it.status}</span>
                </div>
                <div>
                  <Link to={`/admin/enquiries/${it.id}`} className="ga-link" data-testid={`open-${it.id}`}>
                    Open →
                  </Link>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="ga-pager">
        <button
          type="button"
          className="ga-btn ga-btn--ghost"
          disabled={page <= 1}
          onClick={() => setFilter({ page: page - 1 })}
          data-testid="page-prev"
        >
          ← Previous
        </button>
        <span className="ga-mute">Page {page} of {totalPages} · {data.total} total</span>
        <button
          type="button"
          className="ga-btn ga-btn--ghost"
          disabled={page >= totalPages}
          onClick={() => setFilter({ page: page + 1 })}
          data-testid="page-next"
        >
          Next →
        </button>
      </div>
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString();
}
